import { get, set } from '@/helper/localStorage'
import _get from 'lodash/get'
import fields from '@/field/table/brand'

export default {
  namespaced: true,
  state: {
    tableFields: get('brandV2TableFields', fields),
    tableData: [],
    tablePagination: get('brandV2TablePagination'),
    tableKeyword: get('brandV2TableKeyword'),
    tableSort: get('brandV2TableSort'),
    tableFilter: get('brandV2TableFilter', []),
    detailData: get('brandV2DetailData'),
    loadingTable: false,
    loadingDetail: false,
    loadingSubmit: false,
  },
  getters: {
    tableFields: state => {
      if (!state.tableFields) return undefined
      const visibilities = state.tableFields.reduce((obj, item) => Object.assign(obj, { [item.key]: item.visibility }), {})
      return fields.map(item => ({
        ...item,
        visibility: item.key in visibilities ? visibilities[item.key] : item.visibility,
      }))
    },
    tableSort: state => {
      if (!state.tableSort) return undefined
      const symbol = state.tableSort.direction === 'desc' ? '-' : ''
      return `${symbol}${state.tableSort.key}`
    },
    tableData: state => {
      if (!state.tableData) return []
      return state.tableData.map(item => ({ ...item }))
    },
    detailData: state => {
      if (!state.detailData) return undefined
      return { ...state.detailData }
    },
    tableFilter: state => {
      if (!state.tableFilter) return []
      return state.tableFilter
        .reduce((acc, elem) => Object.assign(acc, {
          [`filter[${elem.key}]`]: Array.isArray(elem.value)
            ? elem.value.join(',')
            : elem.value,
        }), {})
    },
    tableParams: (state, getters, rootState) => ({
      'filter[name]': state.tableKeyword,
      ...getters.tableFilter,
      sort: getters.tableSort,
      page: _get(state, 'tablePagination.current_page', 1),
      limit: rootState.appConfig.settings.tablePerPage,
    }),
  },
  mutations: {
    SET_TABLE_FIELDS(state, val) {
      set('brandV2TableFields', val)
      state.tableFields = val
    },
    RESET_TABLE_FIELDS(state) {
      set('brandV2TableFields', fields)
      state.tableFields = fields
    },
    SET_TABLE_DATA(state, val) {
      state.tableData = val
    },
    SET_TABLE_PAGINATION(state, val) {
      set('brandV2TablePagination', val)
      state.tablePagination = val
    },
    SET_TABLE_KEYWORD(state, val) {
      set('brandV2TableKeyword', val)
      state.tableKeyword = val
    },
    SET_DETAIL_DATA(state, val) {
      set('brandV2DetailData', val)
      state.detailData = val
    },
    SET_TABLE_SORT(state, val) {
      set('brandV2TableSort', val)
      state.tableSort = val
    },
    SET_TABLE_FILTER(state, val) {
      set('brandV2TableFilter', val)
      state.tableFilter = val
    },
    SET_LOADING_TABLE(state, val) {
      state.loadingTable = val
    },
    SET_LOADING_DETAIL(state, val) {
      state.loadingDetail = val
    },
    SET_LOADING_SUBMIT(state, val) {
      state.loadingSubmit = val
    },
  },
  actions: {
    getTableData({ commit, getters }) {
      commit('SET_LOADING_TABLE', true)
      const { tableParams: params } = getters
      return this._vm.$http.get('/v2/admin/merchants', { params })
        .then(({ data }) => {
          const rows = (data.data || []).map(item => ({
            ...item,
            is_open: item.is_open ? 'buka' : 'tutup',
            courier_names: Array.isArray(item.courier_merchants)
              ? item.courier_merchants.map(c => c.name).filter(Boolean)
              : [],
          }))
          commit('SET_TABLE_DATA', rows)
          commit('SET_TABLE_PAGINATION', data.meta || {})
        })
        .finally(() => {
          commit('SET_LOADING_TABLE', false)
        })
    },
    getDetail({ commit }, id) {
      commit('SET_LOADING_DETAIL', true)
      return this._vm.$http.get(`/v2/admin/merchants/${id}`)
        .then(({ data }) => {
          const detail = data.data || {}
          commit('SET_DETAIL_DATA', {
            ...detail,
            is_open: detail.is_open ? 'buka' : 'tutup',
            courier_names: Array.isArray(detail.courier_merchants)
              ? detail.courier_merchants.map(c => c.name).filter(Boolean)
              : [],
          })
        })
        .finally(() => {
          commit('SET_LOADING_DETAIL', false)
        })
    },
    create({ commit }, payload) {
      commit('SET_LOADING_SUBMIT', true)

      const body = {
        media_id: payload.image || payload.media_id, // from ImageField
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        password: payload.password,
        description: payload.description,
        is_active: payload.is_active,
        affiliate_percentage: payload.affiliate_percentage,
        order: payload.order,
        is_open: payload.is_open,
        close_desc: payload.close_desc,
        close_started_at: payload.close_started_at,
        close_ended_at: payload.close_ended_at,
      }

      return this._vm.$http.post('/v2/admin/merchants', body)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    update({ commit, state }, { id, data: payload }) {
      commit('SET_LOADING_SUBMIT', true)

      const { detailData } = state
      const body = {}

      if (Object.prototype.hasOwnProperty.call(payload, 'name')
        && payload.name !== detailData.name) {
        body.name = payload.name
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'email')
        && payload.email // do not send empty string; BE requires when present
        && payload.email !== (detailData.email || '')) {
        body.email = payload.email
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'phone')) {
        // map empty string to null to satisfy nullable rule
        const nextPhone = payload.phone === '' ? null : payload.phone
        if (nextPhone !== (detailData.phone ?? null)) {
          body.phone = nextPhone
        }
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'description')
        && (payload.description || '') !== (detailData.description || '')) {
        body.description = payload.description || ''
      }
      // is_active boolean
      const currentIsActive = Object.prototype.hasOwnProperty.call(detailData, 'is_active')
        ? detailData.is_active
        : detailData.active
      if (Object.prototype.hasOwnProperty.call(payload, 'is_active')
        && payload.is_active !== currentIsActive) {
        body.is_active = payload.is_active
      }
      // is_open boolean
      const currentIsOpen = !!detailData.is_open
      if (Object.prototype.hasOwnProperty.call(payload, 'is_open')
        && payload.is_open !== currentIsOpen) {
        body.is_open = payload.is_open
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'affiliate_percentage')
        && payload.affiliate_percentage !== detailData.affiliate_percentage) {
        body.affiliate_percentage = payload.affiliate_percentage
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'order')
        && payload.order !== detailData.order) {
        body.order = payload.order
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'close_desc')
        && (payload.close_desc || '') !== (detailData.close_desc || '')) {
        body.close_desc = payload.close_desc || ''
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'close_started_at')
        && (payload.close_started_at || '') !== (detailData.close_started_at || '')) {
        body.close_started_at = payload.close_started_at || ''
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'close_ended_at')
        && (payload.close_ended_at || '') !== (detailData.close_ended_at || '')) {
        body.close_ended_at = payload.close_ended_at || ''
      }
      if (payload.image) {
        body.media_id = payload.image
      }

      if (Object.keys(body).length === 0) {
        commit('SET_LOADING_SUBMIT', false)
        return Promise.resolve()
      }

      return this._vm.$http.patch(`/v2/admin/merchants/${id}`, body)
        .then(res => {
          const data = res.data?.data || {}
          // keep detail mapping similar to getDetail
          commit('SET_DETAIL_DATA', {
            ...data,
            image: data.image_url,
            email: _get(data, 'user.email', ''),
            phone: _get(data, 'user.phone', ''),
            is_active: data.is_active,
            is_open: !!data.is_open,
            courier_names: Array.isArray(data.courier_merchants)
              ? data.courier_merchants.map(c => c.name).filter(Boolean)
              : [],
          })
        })
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
  },
}
